/**
 * `avg_position` direction — settled by the backend, never derived here.
 *
 * ── The ambiguity this file used to guard against ───────────────────────────────
 * A LOWER position number is a BETTER result. So "up" and "down" each mean two things:
 * "up" is either the number rising (worse) or the park rising on the page (better). The
 * v1 spec contradicted itself on which sign of `position_change_pct` was an improvement,
 * and the live Verde Ranch probe (2026-08-13) could not settle it — that property had 14
 * days of data, so `comparison` was null and no signed example existed. The tile
 * therefore shipped with no arrow and no colour: an unsigned percentage whose meaning is
 * unknown is precisely the landmine.
 *
 * ── Settled, 2026-08-14 (backend addendum §3; re-stated in spec v2 §8) ──────────
 * "Rather than pick a convention, the backend now computes it." `search_headline.facts`
 * carries `position_direction` as one of three strings, or null:
 *
 *   "higher on the results page than a year ago"                → improved
 *   "lower on the results page than a year ago"                 → declined
 *   "in about the same spot on the results page as a year ago"  → neutral
 *   null — no comparison available; show the number and no direction
 *
 * Both documents are explicit that the string is the ONLY input: "Use that string
 * verbatim. Do not derive direction from `avg_position` or `position_change_pct`."
 *
 * ── Why `position_change_pct` is not rendered at all ────────────────────────────
 * The direction is now trustworthy; the percentage's sign convention still is not
 * documented anywhere. Putting the number next to an arrow keyed to the string invites
 * the reader to tie the two together — and a `-18.6%` beside a green up-arrow is a
 * contradiction on screen even when both halves are individually correct. §8 asks for
 * the number and the direction, and that is exactly what the tile shows.
 */

/** The three direction strings, keyed by the tone each one earns. */
const IMPROVED_PREFIX = 'higher';
const DECLINED_PREFIX = 'lower';

/**
 * The verbatim `position_direction` string, or `null` when there is none.
 *
 * Verbatim is the point: it is backend-authored prose written for an owner to read, and
 * paraphrasing it here would reintroduce the interpretation the backend took over.
 *
 * @param {unknown} positionDirection the raw `facts.position_direction`
 * @returns {string|null} the string to render, or null for no direction line
 */
export const positionLabel = (positionDirection) =>
  typeof positionDirection === 'string' && positionDirection.trim() ? positionDirection : null;

/**
 * Tone for the direction string: `'improved' | 'declined' | 'neutral'`.
 *
 * Matched on the leading word rather than on the whole sentence. The backend owns this
 * prose and may reword the tail of it ("than a year ago" → "than last year") without
 * that being a contract change; anchoring on the full string would turn a copy edit into
 * a silently uncoloured tile. Anything unrecognised — including "in about the same
 * spot", and including a string this release has never seen — is neutral, which renders
 * the words with no arrow and no colour. That is the safe failure: §8's list ends with
 * "nothing for the other two".
 *
 * @param {unknown} positionDirection the raw `facts.position_direction`
 * @returns {'improved'|'declined'|'neutral'} tone for colour and glyph
 */
export const positionDirectionTone = (positionDirection) => {
  const label = positionLabel(positionDirection);
  if (!label) return 'neutral';
  const first = label.trim().toLowerCase();
  if (first.startsWith(IMPROVED_PREFIX)) return 'improved';
  if (first.startsWith(DECLINED_PREFIX)) return 'declined';
  return 'neutral';
};
